import { useState } from "react";
import { MapPin, Mail, Phone, Clock, Siren } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/app";
import { Reveal, MaskedLine, Eyebrow } from "../components/Motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const FAQS = [
  {
    q: "Do I need a security clearance to enroll?",
    a: "Not for every program. Foundation and executive courses require only a Public Trust adjudication, which the Academy initiates at enrollment. Advanced operations courses require an active Secret or Top Secret clearance.",
  },
  {
    q: "Can civilian federal employees and contractors attend?",
    a: "Yes. The Academy serves the total federal workforce — civilian agencies, armed forces, and cleared contractor personnel. State, local and critical-infrastructure partners are eligible for designated programs.",
  },
  {
    q: "Are online or hybrid formats available?",
    a: "Yes. Several tracks are delivered fully online through the virtual academy, and most in-person programs offer a hybrid option. The National Cyber Range labs are always conducted in person.",
  },
  {
    q: "How is training funded?",
    a: "Most candidates attend under agency training budgets, individual development plans, or workforce development initiatives. Your agency training officer can confirm available funding authorities with our admissions office.",
  },
  {
    q: "What certification do graduates receive?",
    a: "Graduates receive an Academy certificate of proficiency with documented CPE credits, plus a competency record mapped to the federal cyber workforce framework and relevant control families.",
  },
];

const SUBJECTS = ["Enrollment Advising", "General Inquiry", "Press & Media", "Vulnerability Disclosure", "Governance Board"];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", subject: SUBJECTS[0], message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/inquiries", form);
      toast.success(res.data.message);
      setForm({ name: "", email: "", organization: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please verify your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative bg-navy-950 grid-lines overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-44 pb-20 lg:pt-52 lg:pb-24">
          <MaskedLine as="div" delay={0.3} className="mb-6">
            <Eyebrow light>Contact & Admissions</Eyebrow>
          </MaskedLine>
          <h1 className="font-serif font-bold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl" data-testid="contact-headline">
            <MaskedLine delay={0.45}>Open a secure</MaskedLine>
            <MaskedLine delay={0.57}>
              <em className="text-gold-300 italic">line of inquiry.</em>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.7} className="mt-8 max-w-2xl">
            <p className="text-base sm:text-lg leading-relaxed text-slate-300">
              Admissions, partnerships, press and vulnerability disclosures are
              handled by the Office of the Superintendent. A program officer
              will respond within three business days.
            </p>
          </MaskedLine>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="contact-main-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="inquiry-form"
              className="bg-white border border-paper-border p-8 lg:p-10"
            >
              <h2 className="font-serif font-semibold text-2xl text-navy-900">Official Correspondence</h2>
              <p className="mt-2 text-sm text-slate-600">
                All fields marked required are processed under the Privacy Act of 1974.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="field-label" htmlFor="inq-name">Full Name</label>
                  <input id="inq-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field-input" placeholder="Jane A. Doe" data-testid="inquiry-name-input" />
                </div>
                <div>
                  <label className="field-label" htmlFor="inq-email">Email</label>
                  <input id="inq-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-input" placeholder="jane.doe@agency.gov" data-testid="inquiry-email-input" />
                </div>
                <div>
                  <label className="field-label" htmlFor="inq-org">Agency / Organization</label>
                  <input id="inq-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="field-input" placeholder="Optional" data-testid="inquiry-org-input" />
                </div>
                <div>
                  <label className="field-label" htmlFor="inq-subject">Subject</label>
                  <select id="inq-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="field-input" data-testid="inquiry-subject-select">
                    {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label" htmlFor="inq-message">Message</label>
                  <textarea id="inq-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="field-textarea" placeholder="How can the Academy serve your mission?" data-testid="inquiry-message-input" />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit-button"
                className="mt-8 w-full h-12 rounded-full bg-navy-900 text-gold-300 text-sm font-semibold tracking-wide transition-all duration-200 hover:bg-navy-800 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:pointer-events-none"
              >
                {submitting ? "Transmitting…" : "Submit Correspondence"}
              </button>
            </form>
          </Reveal>

          {/* HQ & channels */}
          <div className="lg:col-span-5 space-y-6">
            <Reveal delay={0.1}>
              <div className="federal-frame relative overflow-hidden bg-navy-950 p-8 lg:p-10" data-testid="hq-card">
                <div className="absolute inset-0 grid-lines opacity-50" />
                <div className="relative">
                  <h3 className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold-400">Headquarters</h3>
                  <div className="mt-6 space-y-5 text-sm text-slate-200">
                    <p className="flex gap-3">
                      <MapPin className="w-4 h-4 text-gold-500 shrink-0 mt-0.5" />
                      <span>United States Federal Agencies Directory<br />1200 Constitution Row NW<br />Federal Triangle, Washington, D.C. 20004</span>
                    </p>
                    <p className="flex gap-3 items-center">
                      <Mail className="w-4 h-4 text-gold-500 shrink-0" />
                      info@usa.gov
                    </p>
                    <p className="flex gap-3 items-center">
                      <Clock className="w-4 h-4 text-gold-500 shrink-0" />
                      Monday–Friday · 0800–1700 ET
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="bg-white border-2 border-federalred/30 p-8" data-testid="incident-card">
                <div className="flex items-center gap-3">
                  <Siren className="w-5 h-5 text-federalred" />
                  <h3 className="font-serif font-semibold text-lg text-navy-900">Incident Response Line</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  For active cyber incidents affecting federal systems, contact
                  the 24-hour watch floor. Do not transmit classified details
                  over this channel.
                </p>
                <p className="mt-4 font-mono text-lg font-semibold tracking-wider text-navy-900">1-844-872-4681</p>
              </div>
            </Reveal>

            <Reveal delay={0.24}>
              <div className="bg-paper-100 border border-paper-border p-8" data-testid="map-card">
                <div className="h-36 bg-navy-950 grid-lines relative overflow-hidden flex items-center justify-center">
                  <span className="chip border-gold-500/50 text-gold-300 bg-navy-950/70 backdrop-blur-sm">
                    38.8851° N · 77.0284° W
                  </span>
                </div>
                <p className="mt-4 text-xs font-mono uppercase tracking-[0.16em] text-slate-500">
                  Federal Triangle · Washington, D.C.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-paper-100 border-t border-paper-border" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <Eyebrow>Frequently Asked Questions</Eyebrow>
          </Reveal>
          <MaskedLine as="h2" inView delay={0.08} className="mt-4 font-serif font-semibold tracking-tight text-3xl sm:text-4xl text-navy-900">
            Before you write to us.
          </MaskedLine>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-paper-border" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-serif text-lg text-navy-900 hover:text-gold-600 hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600 pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
